<div class="p-6 max-w-6xl mx-auto">
  <!-- Header -->
  <div class="mb-6">
    <h1 class="text-2xl font-bold text-gray-800">Identity Documents</h1>
    <p class="text-gray-600 mt-1">Upload and manage your identity verification documents</p>
  </div>

  <!-- Progress Card -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" *ngIf="documentStatus">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-800">Document Upload Progress</h3>
        <p class="text-sm text-gray-500">
          {{ documentStatus.uploadedMandatory || 0 }} of {{ documentStatus.totalMandatory || 0 }} mandatory documents uploaded
        </p>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-2xl font-bold" 
              [class.text-green-600]="documentStatus.allMandatoryUploaded"
              [class.text-yellow-600]="!documentStatus.allMandatoryUploaded">
          {{ getProgressPercentage() }}%
        </span>
      </div>
    </div>
    
    <!-- Progress Bar -->
    <div class="w-full bg-gray-200 rounded-full h-3">
      <div class="h-3 rounded-full transition-all duration-500"
           [class.bg-green-500]="documentStatus.allMandatoryUploaded"
           [class.bg-yellow-500]="!documentStatus.allMandatoryUploaded"
           [style.width.%]="getProgressPercentage()">
      </div>
    </div>

    <!-- Pending Documents Alert -->
    <div *ngIf="documentStatus.pendingUpload && documentStatus.pendingUpload.length > 0" 
         class="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div class="flex items-start gap-3">
        <mat-icon class="text-yellow-600">warning</mat-icon>
        <div>
          <h4 class="font-medium text-yellow-800">Documents Pending Upload</h4>
          <ul class="mt-2 text-sm text-yellow-700 list-disc list-inside">
            <li *ngFor="let doc of documentStatus.pendingUpload">{{ doc.name }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="flex items-center justify-center py-12">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <!-- Documents Grid -->
  <div *ngIf="!loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <!-- Document Card for each requirement -->
    <div *ngFor="let req of requirements" 
         class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      
      <!-- Card Header -->
      <div class="p-4 border-b border-gray-100" 
           [class.bg-green-50]="getDocumentByType(req.document_type)?.verification_status === 'verified'"
           [class.bg-yellow-50]="getDocumentByType(req.document_type)?.verification_status === 'pending'"
           [class.bg-red-50]="getDocumentByType(req.document_type)?.verification_status === 'rejected'"
           [class.bg-gray-50]="!getDocumentByType(req.document_type)">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center"
                 [class.bg-green-100]="getDocumentByType(req.document_type)?.verification_status === 'verified'"
                 [class.bg-yellow-100]="getDocumentByType(req.document_type)?.verification_status === 'pending'"
                 [class.bg-red-100]="getDocumentByType(req.document_type)?.verification_status === 'rejected'"
                 [class.bg-gray-100]="!getDocumentByType(req.document_type)">
              <mat-icon [class.text-green-600]="getDocumentByType(req.document_type)?.verification_status === 'verified'"
                        [class.text-yellow-600]="getDocumentByType(req.document_type)?.verification_status === 'pending'"
                        [class.text-red-600]="getDocumentByType(req.document_type)?.verification_status === 'rejected'"
                        [class.text-gray-600]="!getDocumentByType(req.document_type)">
                {{ getStatusIcon(getDocumentByType(req.document_type)?.verification_status || 'not_uploaded') }}
              </mat-icon>
            </div>
            <div>
              <h3 class="font-semibold text-gray-800">{{ req.document_name }}</h3>
              <div class="flex items-center gap-2 mt-1">
                <span *ngIf="req.is_mandatory" 
                      class="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                  Required
                </span>
                <span *ngIf="!req.is_mandatory" 
                      class="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                  Optional
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Card Body -->
      <div class="p-4">
        <!-- Uploaded Document Info -->
        <div *ngIf="getDocumentByType(req.document_type) as doc">
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500">Status:</span>
              <span class="px-2 py-0.5 rounded-full text-xs font-medium"
                    [ngClass]="getStatusClass(doc.verification_status)">
                {{ doc.verification_status | titlecase }}
              </span>
            </div>
            <div *ngIf="doc.document_number" class="flex justify-between">
              <span class="text-gray-500">Document No:</span>
              <span class="font-medium">{{ doc.document_number }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Uploaded:</span>
              <span class="font-medium">{{ formatDate(doc.created_at) }}</span>
            </div>
            <div *ngIf="doc.expiry_date" class="flex justify-between">
              <span class="text-gray-500">Expires:</span>
              <span class="font-medium">{{ formatDate(doc.expiry_date) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">File Size:</span>
              <span class="font-medium">{{ formatFileSize(doc.file_size) }}</span>
            </div>
          </div>

          <!-- Rejection Reason -->
          <div *ngIf="doc.verification_status === 'rejected' && doc.rejection_reason" 
               class="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-700">
              <strong>Rejection Reason:</strong> {{ doc.rejection_reason }}
            </p>
          </div>

          <!-- Verified By Info -->
          <div *ngIf="doc.verification_status === 'verified' && doc.verified_by_name" 
               class="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p class="text-sm text-green-700">
              Verified by <strong>{{ doc.verified_by_name }}</strong> on {{ formatDate(doc.verified_at || '') }}
            </p>
          </div>

          <!-- Action Buttons -->
          <div class="mt-4 flex flex-wrap gap-2">
            <button mat-stroked-button color="primary" (click)="viewDocument(doc)" class="flex items-center gap-1">
              <mat-icon class="text-sm">visibility</mat-icon>
              View
            </button>
            
            <!-- Admin Verification Buttons -->
            <ng-container *ngIf="isAdmin && doc.verification_status === 'pending'">
              <button mat-flat-button color="primary" (click)="verifyDocument(doc, 'verified')" class="flex items-center gap-1">
                <mat-icon class="text-sm">check</mat-icon>
                Verify
              </button>
              <button mat-flat-button color="warn" (click)="verifyDocument(doc, 'rejected')" class="flex items-center gap-1">
                <mat-icon class="text-sm">close</mat-icon>
                Reject
              </button>
            </ng-container>

            <!-- Delete Button (only for non-verified) -->
            <button *ngIf="doc.verification_status !== 'verified'" 
                    mat-stroked-button color="warn" 
                    (click)="deleteDocument(doc)" 
                    class="flex items-center gap-1">
              <mat-icon class="text-sm">delete</mat-icon>
              Delete
            </button>
          </div>
        </div>

        <!-- Upload Form (when no document uploaded or rejected) -->
        <div *ngIf="!getDocumentByType(req.document_type) || getDocumentByType(req.document_type)?.verification_status === 'rejected'">
          <p *ngIf="req.description" class="text-sm text-gray-500 mb-4">{{ req.description }}</p>
          
          <!-- Document Number Input -->
          <div *ngIf="needsDocumentNumber(req.document_type)" class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Document Number</label>
            <input type="text" 
                   [(ngModel)]="documentNumber"
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   placeholder="Enter document number"
                   [disabled]="selectedDocumentType !== req.document_type">
          </div>

          <!-- Expiry Date Input -->
          <div *ngIf="needsExpiryDate(req.document_type)" class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
            <input type="date" 
                   [(ngModel)]="expiryDate"
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   [disabled]="selectedDocumentType !== req.document_type">
          </div>

          <!-- File Upload -->
          <div class="relative">
            <input type="file" 
                   [id]="'file-' + req.document_type"
                   class="hidden"
                   accept=".pdf,.jpg,.jpeg,.png"
                   (change)="onFileSelected($event, req.document_type)">
            
            <label [for]="'file-' + req.document_type"
                   class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                   [class.border-blue-500]="selectedDocumentType === req.document_type"
                   [class.bg-blue-50]="selectedDocumentType === req.document_type">
              
              <!-- Preview for selected file -->
              <ng-container *ngIf="selectedDocumentType === req.document_type && selectedFile">
                <img *ngIf="previewUrl" [src]="previewUrl" class="h-20 object-contain rounded">
                <div *ngIf="!previewUrl" class="flex items-center gap-2 text-blue-600">
                  <mat-icon>description</mat-icon>
                  <span class="text-sm font-medium">{{ selectedFile.name }}</span>
                </div>
              </ng-container>
              
              <!-- Default Upload UI -->
              <ng-container *ngIf="selectedDocumentType !== req.document_type || !selectedFile">
                <mat-icon class="text-gray-400 text-4xl">cloud_upload</mat-icon>
                <p class="mt-2 text-sm text-gray-500">Click to upload or drag and drop</p>
                <p class="text-xs text-gray-400">PDF, JPG, PNG (max 5MB)</p>
              </ng-container>
            </label>
          </div>

          <!-- Upload Button -->
          <button *ngIf="selectedDocumentType === req.document_type && selectedFile"
                  mat-flat-button color="primary"
                  class="w-full mt-3"
                  [disabled]="uploading"
                  (click)="uploadDocument()">
            <mat-spinner *ngIf="uploading" diameter="20" class="mr-2"></mat-spinner>
            {{ uploading ? 'Uploading...' : 'Upload Document' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="!loading && requirements.length === 0" 
       class="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
    <mat-icon class="text-gray-400 text-6xl mb-4">folder_open</mat-icon>
    <h3 class="text-lg font-medium text-gray-600">No Document Requirements</h3>
    <p class="text-gray-500 mt-1">No document requirements have been configured for your organization.</p>
  </div>
</div>
